/* global gdAlertify */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { formatMoney } from "../../utils/utils";
import { mensajeError } from "./enviosUi";
import { TIPOS_HONORARIOS, cargarFilasHonorarios, datosHonorarios, mesActual, nombreDe, urlImagenHonorarios } from "./honorariosWsp";
import MonthInput from "../components/MonthInput";

/**
 * Prueba de la plantilla de honorarios: elige período, tipo (adelanto / fin de mes) y auxiliar.
 * La imagen (tabla del "Resumen de Pago") la genera el backend en PHP, igual que en el envío masivo:
 * aquí solo se muestra la vista previa y se entrega la receta + datos para las variables.
 */
const HonorariosPrueba = ({ onListo }) => {
    const [periodo, setPeriodo] = useState(mesActual());
    const [tipo, setTipo] = useState("fm");
    const [filas, setFilas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [auxiliarId, setAuxiliarId] = useState(null);
    const [generando, setGenerando] = useState(false);
    const [miniatura, setMiniatura] = useState(null);
    const generacion = useRef(0);

    useEffect(() => () => { if (miniatura) URL.revokeObjectURL(miniatura); }, [miniatura]);

    const limpiar = () => {
        setFilas([]);
        setAuxiliarId(null);
        setMiniatura(null);
    };

    const cargarAuxiliares = async () => {
        if (!periodo) return;
        setCargando(true);
        setAuxiliarId(null);
        setMiniatura(null);
        try {
            const lista = await cargarFilasHonorarios(periodo, tipo);
            setFilas(lista);
            if (!lista.length) gdAlertify.notiWarning("No hay honorarios para ese período.");
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudieron cargar los honorarios del período."));
        } finally {
            setCargando(false);
        }
    };

    const opciones = useMemo(() => filas
        .map((row) => {
            const datos = datosHonorarios(row, tipo, periodo);
            return { value: Number(row.auxiliar_id), label: `${nombreDe(row)} · $${formatMoney(datos.total)}`, row, datos };
        })
        .sort((a, b) => a.label.localeCompare(b.label)), [filas, tipo, periodo]);

    const seleccion = opciones.find((o) => o.value === auxiliarId) || null;

    // Al elegir auxiliar se pide la vista previa al backend.
    useEffect(() => {
        if (!seleccion) return undefined;
        const id = ++generacion.current;
        (async () => {
            setGenerando(true);
            setMiniatura(null);
            try {
                const url = await urlImagenHonorarios(periodo, tipo, seleccion.value);
                if (id !== generacion.current) {
                    URL.revokeObjectURL(url);
                    return;
                }
                setMiniatura(url);
                onListo({
                    honorarios: { periodo, tipo, auxiliar_id: seleccion.value },
                    miniatura: url,
                    auxiliar: nombreDe(seleccion.row),
                    telefonoAuxiliar: seleccion.row.fono_auxiliar || null,
                    variables: seleccion.datos.variables,
                });
            } catch (error) {
                if (id === generacion.current) gdAlertify.notiError(error.message);
            } finally {
                if (id === generacion.current) setGenerando(false);
            }
        })();
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auxiliarId, filas]);

    return (
        <div className="border rounded p-3 mb-3 bg-light">
            <div className="fw-semibold mb-2"><i className="bi bi-person-badge me-2"></i>Datos de honorarios de una auxiliar</div>
            <div className="row g-2 align-items-end mb-2">
                <div className="col-sm-4">
                    <label className="form-label small mb-1">Período</label>
                    <MonthInput className="form-control form-control-sm" value={periodo}
                        onChange={(e) => { setPeriodo(e.target.value); limpiar(); }} />
                </div>
                <div className="col-sm-5">
                    <label className="form-label small mb-1">Tipo de pago</label>
                    <select className="form-select form-select-sm" value={tipo} onChange={(e) => { setTipo(e.target.value); limpiar(); }}>
                        {Object.entries(TIPOS_HONORARIOS).map(([valor, def]) => <option key={valor} value={valor}>{def.label}</option>)}
                    </select>
                </div>
                <div className="col-sm-3">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={cargarAuxiliares} disabled={cargando || !periodo}>
                        {cargando ? <span className="spinner-border spinner-border-sm"></span> : "Cargar"}
                    </button>
                </div>
            </div>

            {filas.length > 0 && (
                <Select
                    className="mb-2"
                    placeholder="Busca la auxiliar…"
                    options={opciones}
                    value={seleccion}
                    onChange={(o) => setAuxiliarId(o ? o.value : null)}
                    isClearable
                />
            )}

            {generando && (
                <div className="small text-muted mb-2">
                    <span className="spinner-border spinner-border-sm me-2"></span>Generando la imagen…
                </div>
            )}
            {miniatura && !generando && (
                <div className="mb-2">
                    <div className="small text-success mb-1"><i className="bi bi-check2-circle me-1"></i>Así se verá la imagen (se genera de nuevo al enviar):</div>
                    <a href={miniatura} target="_blank" rel="noreferrer">
                        <img src={miniatura} alt="Imagen de honorarios" className="img-thumbnail" style={{ maxHeight: 220 }} />
                    </a>
                </div>
            )}

            <div className="form-text">
                La imagen es el <strong>Resumen de Pago</strong> de Honorarios, generado en el servidor. Las variables se llenan con
                nombre, período, tipo de pago y total a pagar.
            </div>
        </div>
    );
};

export default HonorariosPrueba;
